import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SlitWork, FileRecord } from '@twinsketch/topika-model';
import ErrorModal from 'src/components/common/ErrorModal';
import 'src/styles/workOrder/work-order.css';

interface WorkOrderSlitWorkFormProps {
  slitWorks: SlitWork[];
  files: FileRecord[];
}

const WorkOrderSlitWorkForm: React.FC<WorkOrderSlitWorkFormProps> = ({ slitWorks, files }) => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [expandedIndices, setExpandedIndices] = useState<Set<number>>(new Set());

  const toggleExpand = (index: number) => {
    setExpandedIndices(prev => {
      const updated = new Set(prev);
      if (updated.has(index)) {
        updated.delete(index);
      } else {
        updated.add(index);
      }
      return updated;
    });
  };

  return (
    <div className="content-wrapper">
      <ErrorModal message={error} onClose={() => setError(null)} />
      <h2>Slitting Summary</h2>

      <div className="table-wrapper">
        <table className="mixing-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th></th>
              <th>Date</th>
              <th>Operators</th>
              <th>Project</th>
              <th>Total Output (m)</th>
            </tr>
          </thead>
          <tbody>
            {slitWorks.length > 0 ? (
              slitWorks.map((work, index) => (
                <React.Fragment key={index}>
                  <tr>
                    <td>
                      <button
                        onClick={() => toggleExpand(index)}
                        aria-label={`Toggle details for entry ${index + 1}`}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '1rem',
                          color: 'brown',
                        }}
                      >
                        {expandedIndices.has(index) ? '−' : '+'}
                      </button>
                    </td>
                    <td>{work.date ?? '—'}</td>
                    <td>{work.operators?.join(' | ') ?? '—'}</td>
                    <td>{work.projectName ?? '—'}</td>
                    <td>{work.totalOutputLengthMeter ?? '—'}</td>
                  </tr>

                  {expandedIndices.has(index) && (
                    <tr>
                      <td colSpan={5} style={{ padding: 0 }}>
                        <div
                          style={{
                            padding: '12px',
                            backgroundColor: '#fafafa',
                            borderTop: '1px solid #ccc',
                          }}
                        >
                          <strong style={{ display: 'block', marginBottom: '8px' }}>
                            Slit Batches
                          </strong>
                          <table>
                            <thead>
                              <tr>
                                <th>Batch No</th>
                                <th>Lot No</th>
                                <th>Input (m)</th>
                                <th>Good Output (m)</th>
                                <th>Speed</th>
                                <th>UN-W</th>
                                <th>RE-W (Top)</th>
                                <th>RE-W (Bottom)</th>
                                <th>Incidence Angle</th>
                                <th>Emission Angle Lower</th>
                              </tr>
                            </thead>
                            <tbody>
                              {work.slitBatches.map((batch, bIndex) => (
                                <tr key={bIndex}>
                                  <td>{batch.no}</td>
                                  <td>{batch.coatingLotNo.join(', ')}</td>
                                  <td>{batch.inputLengthMeter}</td>
                                  <td>{batch.acceptedOutputLengthMeter}</td>
                                  <td>{batch.speedMeterPerMin}</td>
                                  <td>{batch.unwindTensionN}</td>
                                  <td>{batch.rewindUpperTensionN}</td>
                                  <td>{batch.rewindLowerTensionN}</td>
                                  <td>{batch.incidenceAngle}</td>
                                  <td>{batch.emissionAngleLower}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center', color: 'gray' }}>
                  No Slit Work Available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WorkOrderSlitWorkForm;
